/**
 * Terms of Use
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/_public/terms-of-use")({
  component: TermsOfUse,
});

function TermsOfUse() {
  return (
    <div>
      <title>Terms of Use - Legrande Health Portal</title>
      <Card>
        <CardHeader>
          <div className="flex justify-center gap-4">
            <CardTitle>
              <Link className="text-primary underline" to="/terms-of-use">
                Terms of Use
              </Link>
            </CardTitle>
            <Separator orientation="vertical" className="h-6!" />
            <CardTitle>
              <Link to="/privacy-policy">Privacy Policy</Link>
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p>Last Updated: 5-13-19</p>
          <p>
            PLEASE CAREFULLY READ THESE TERMS OF USE ("<b>TERMS</b>"). BY USING
            THE SERVICES (DEFINED BELOW), YOU AGREE TO BE BOUND BY THESE TERMS.
            IF YOU DO NOT AGREE, DO NOT USE THE WEBSITE OR CLICK TO INDICATE
            THAT YOU AGREE TO THESE TERMS.{" "}
          </p>
          <p className="font-semibold">
            THESE TERMS CONTAIN A MANDATORY ARBITRATION PROVISION AND CLASS
            ACTION WAIVER. PLEASE REVIEW SECTION 26. UNLESS YOU OPT OUT OF
            ARBITRATION WITHIN 30 DAYS OF THE DATE YOU FIRST AGREE TO THESE
            TERMS BY FOLLOWING THE OPT-OUT PROCEDURE SPECIFIED IN THE
            "ARBITRATION" SECTION BELOW, AND EXCEPT AS SET FORTH BELOW, YOU
            AGREE THAT DISPUTES BETWEEN YOU AND LEGRANDE WILL BE RESOLVED BY
            BINDING, INDIVIDUAL ARBITRATION AND YOU ARE WAIVING YOUR RIGHT TO A
            TRIAL BY JURY OR TO PARTICIPATE AS A PLAINTIFF OR CLASS MEMBER IN
            ANY PURPORTED CLASS ACTION OR REPRESENTATIVE PROCEEDING.{" "}
          </p>
          <p>
            These Terms are a legal agreement between Legrande Corporation, a
            California Corporation <b>("Legrande," "we," "us," or "our")</b> and
            you that governs your use of the Services and any related
            applications, including all Updates, related documentation, and
            information and content accessed through the Services. By using the
            Services, you authorize the collection of information about you,
            your use of the Services, the device on which the Services is
            installed, and the use, transmission, processing, and storing of
            information as described in Legrande's Privacy Policy (located at
            legrandehealth.app/privacy-policy). By using our Services, you agree
            to this Privacy Policy.
          </p>
          <p>
            By using the Services, you agree that Legrande may use your email to
            communicate with you regarding all agreements, services, billing,
            advertising, notices, disclosures and other communications that we
            provide to you electronically. You also agree that such email
            communication will satisfy any legal requirement that such
            communications be in writing.{" "}
          </p>
          <ol className="list-decimal flex flex-col gap-4 ml-4">
            <li>
              <b>The Legrande Services.</b>
              <br />
              Legrande provides an online prescription management and delivery
              service through its website and mobile application ("Services").
              Legrande enables doctors to prescribe and send to a Participating
              Dispensary an order ("Order") for one or more products (including
              prescription medications, over-the-counter products, supplements,
              and other products (each a "Product")). Legrande acts as an
              e-prescribing and prescription management Services only, Legrande
              does not sell any Products directly. Each prescription product
              that you order through Legrande must be based on a valid
              prescription issued for a legitimate medical purpose by a medical
              professional acting in the usual course of their professional
              practice. The Services is not, and is not intended to be, a
              substitute for professional medical advice, diagnosis or
              treatment, and any information that is not provided directly from
              your doctor to you through the Services does not constitute
              medical or other professional advice. The information provided
              through Legrande's Services is designed to support, not replace,
              the relationship that exists between patient/site-visitor and
              their doctor. Never disregard professional medical advice or delay
              in seeking it because of something you have read or seen from our
              Services.{" "}
            </li>
            <li>
              <b>Eligibility.</b>
              <br />
              You must be a resident of the United States and 18 years of age or
              older to use the Services. By using the Services, you certify that
              you are 18 years of age or older and a United States resident.
            </li>
            <li>
              <b>Account Registration; Username and Password.</b>
              <br />{" "}
              <p>
                In order to use the Services you will need to log in to the
                Services with a user account that you register for through the
                Services ("<b>Account</b>"). ou agree to provide current,
                complete, and accurate information when you register for an
                Account. You agree to keep your log-in information confidential
                and not authorize any third party to use it or your Account. You
                agree that Legrande may attribute all use of your Account to you
                and that you are responsible for all activities that occur under
                your Account. You agree to notify Legrande immediately at
                support@legrandehealth.com if you suspect any unauthorized use
                of your Account or any other breach of security. Depending on
                your Account type or other criteria, your Account may not have
                full access to all features or functionality available through
                the Services. You agree not to attempt to access any restricted
                features or functionality. When you place an Order through
                Legrande, we work with a pharmacy (whether retail, mail-order,
                compounding, or specialized), practitioner (whether doctor,
                naturopath, optometrist, or otherwise), or other dispenser
                (each, a "<b>Participating Dispensary</b>") that processes your
                Order and fills your prescriptions. You acknowledge and agree
                that Legrande is not a dispensary or pharmacy and doesn't fill
                prescriptions ourselves nor do we manufacture, prepare, dispense
                or provide medications of any kind. You also acknowledge and
                agree that, although we may select certain dispensaries to work
                with based on various metrics we apply, our decision to work
                with a specific dispensaries should not be deemed as an
                endorsement of, or a warranty regarding the quality of services
                provided by, such dispensary. If, for any reason, you aren't
                satisfied with the manner in which a Participating Dispensary
                processed your Order, you agree that your sole remedy is to
                address this issue with the Participating Dispensary
                directly.{" "}
              </p>
              <p className="my-4">
                Most communication with Legrande occurs through your portal on
                the Services, and it may contain Protected Health Information
                ("PHI") as defined under the Health Insurance Portability and
                Accountability Act, as amended ("HIPAA"), such as pictures of
                your prescriptions, medical insurance cards, and other sensitive
                health information. As a result, it is of utmost importance that
                you keep your portal secure at all times. To the extent there is
                any conflict between these terms of use and our applicable
                obligations under HIPAA, we will comply with our obligations
                under HIPAA.{" "}
              </p>
              <p className="my-4">
                You also acknowledge and agree that we do not provide any
                medical advice, opinion, diagnosis or treatment, and that that
                no dispensary-patient relationship or doctor-patient
                relationship is, or will be, established between Legrande and
                you as a result of your use of the Services or otherwise. You
                should check product information (including package inserts)
                regarding dosages, precautions, warnings, interactions and
                contraindications before administering or using any medications.
                If you have any questions regarding your prescriptions, you
                should consult your healthcare professional and dispensary,
                whose contact information may be listed on the prescription, the
                packaging of your prescription medicine or under your
                Account.{" "}
              </p>
            </li>
            "
            <li>
              <b>Additional Users of the Services</b>
              <br />
              You may add your family members 18 years of age or older as users
              of the Services if you acknowledge that you are acting as this
              patient's representative and have an express authorization from
              the patient to enter their personal information and have
              medications delivered to the address entered. You hereby fully
              release or discharge Legrande and Participating Dispensary from
              any and all claims from injuries, damages and losses your minor
              child may have, arising out of, connected with, incidental to, or
              in any way associated with the administering of medication if you
              use the Services for a minor child.{" "}
            </li>
            <li>
              <b>Legrande Does Not Provide Medical Advice.</b>
              <br />
              <p>
                The Services and all content, information, graphics, text
                images, and other materials accessible through the Services
                (collectively, "<b>Content</b>"), including any Content uploaded
                or posted by users ("<b>User Content</b>") are for information
                and educational purposes only and are not a substitute for the
                professional judgment of a health care professional in
                diagnosing and treating patients. Neither the Services nor the
                Content is intended to be used for medical diagnosis or
                treatment, and Legrande does not warrant or represent that the
                Content is appropriate or safe for your particular health needs,
                even if the information comes from a health care provider using
                the Services. By using the Services, you assume full
                responsibility for the use of the Content and agree that
                Legrande is not responsible or liable for any claim, loss, or
                damage arising from the use of the Content. Legrande does not
                recommend or endorse any specific drugs, tests, physicians,
                products, procedures, opinions, "off-label" drug uses, or other
                Content that may be presented on the Services.
              </p>
              <p className="my-4">
                Your doctor may request that we include certain medical
                information in the package provided with your products. We
                expressly disclaim any responsibility for such materials and
                only provided them to you with your agreement that Legrande will
                not be held liable for any issue related to such content,
                including any mistake made by our Participating Dispensary or
                glitch in our software.
              </p>
              <p className="my-4">
                IF YOU ARE HAVING A MEDICAL EMERGENCY, DO NOT USE OUR SERVICES –
                CALL 911.{" "}
              </p>
            </li>
            <li>
              <b>Authorization to Transfer a Prescription.</b>
              <br />
              <p>
                In case your prescription was transferred to or originally
                filled by a non-participating dispensary or pharmacy (not within
                Legrande's network) Legrande may facilitate the transfer of your
                prescription to a Participating Dispensary. By using Legrande's
                services, you authorize Legrande to facilitate the transfer of
                your prescription to any Participating Dispensary. Legrande has
                the sole discretion to determine which Participating Dispensary
                or fills your prescription. Additionally, in order to provide
                you the best possible quality of service, you authorize Legrande
                to facilitate the transfer of your prescription from one
                Legrande Participating Dispensary to another.{" "}
              </p>
            </li>
            <li>
              <b>Delivery of Your Order.</b>
              <br />
              <p>
                If no one is present at your delivery address at the time of the
                delivery to receive your Order, we, or our delivery partner, may
                leave your Order at your door or other secure location in
                accordance with the general guidelines issued by the delivery
                services, if we, or our delivery partner, believes it is
                reasonable to do so.
              </p>
            </li>
            <li>
              <b>Payment.</b>
              <br />
              <p>
                Legrande does not currently charge users to use the Service
                itself, although it may institute fees upon sixty (60) days
                notice.{" "}
              </p>{" "}
              <p className="my-4">
                You are responsible for paying for all Orders submitted through
                the Service. When you submit an Order through the Service, we,
                in combination with our Participating Dispensary(s), will
                calculate the total Order price, which will be based on: (i) the
                actual cash price charged by the Participating Dispensary for
                each product if you are not utilizing your health insurance for
                such product; (ii) a copay amount that you are required to pay
                for your products, if you are using health insurance coverage
                for such product; (iii) any applicable payment based on a
                coupons being utilized for your products; (iv) any applicable
                delivery and handling fee that we may charge for delivery of the
                medication to the address specified in your Order and/or (v)
                other factors.{" "}
              </p>{" "}
              <p className="my-4"></p>{" "}
              <p className="my-4">
                <b>
                  After we and our Participating Dispensary determines the total
                  Order price, we will send you an email and/or text to notify
                  you of the Order Price. If you do not confirm the initial
                  Order (by clicking the "Approve" button in the email) within
                  seventy-two (72) hours of the time the email was sent to you,
                  your Order will be canceled automatically. We will charge you
                  only when you confirm the initial Order within the seventy-two
                  (72) hour period.
                </b>
              </p>{" "}
              <p className="my-4">
                <b>
                  For recurring Orders for Products that you take on an ongoing
                  basis, we will send you a text or email approximately
                  seventy-two (72) hours before we process any refill for such
                  Orders. We will charge you for any refills unless you cancel
                  within the seventy-two (72) hour period. All refills shall be
                  charged to your payment card on file. If the price for a
                  recurring Order has increased, we will send you another email
                  to confirm the higher price or copay amount. If the price or
                  copay amount for a recurring Order has decreased, we will
                  charge you the lower price or copay amount without explicitly
                  notifying you of the decrease.{" "}
                </b>
              </p>{" "}
              <p className="my-4">
                <b>
                  Once you sign up for an automatic prescription refill through
                  the Service, You agree and acknowledge that your credit card
                  on file will continuously and automatically be charged for
                  each renewal (at the frequency set forth on your prescription)
                  without any action on your part until cancelled as outlined
                  herein. If you wish to cancel, you must call us at the number
                  indicated on the Service prior to the next refill period.
                  There are no refunds for Orders cancelled after a refill has
                  been processed by the Participating Dispensary. Except as
                  permitted herein, all automatic prescription refills are
                  non-cancellable, non-refundable, and non-returnable. You must
                  provide us (or, if applicable, the Participating Dispensary)
                  with a valid credit card and must update it with any changes.
                  If timely payment is not received or cannot be charged to your
                  credit card for any reason, we reserve the right to either
                  suspend or terminate your access to the Services.
                </b>
              </p>{" "}
              <p className="my-4">
                If you place an Order via the Services, you agree (i) that
                Legrande or Legrande's third party payment processing service
                provider may process and/or charge your credit card (and on a
                recurring basis for recurring products) for verification,
                pre-authorization and payment purposes; (ii) to bear any
                additional charges that your bank or other financial service
                provider may levy on you as well as any fees or taxes that may
                apply to you, and that (iii) Legrande may use your insurance
                information to run a test claim to determine the price of the
                medication and/or whether a Prior Authorization is needed.
                You'll receive a confirmation by email once the Participating
                Dispensary has completed processing your Order. If you have any
                concerns or objections regarding any charges, you agree to raise
                them first with Legrande and you agree not to cancel or reject
                any credit card charges unless you have made a reasonable
                attempt at resolving the matter directly with Legrande, the
                Participating Dispensary.
              </p>{" "}
              <p className="my-4">
                By pressing the Approve button, you approve for the Service to
                submit the amount listed for payment on your credit card and
                consent to the storage of your credit card information. You
                certify that you are an authorized user of this credit card and
                that you will not dispute the payment with the credit card
                company so long as the transaction corresponds to the terms
                indicated on the payment page and in the Terms. You acknowledge
                that if any charges to the credit card are declined or charge
                backs are claimed against any outstanding amount, you will still
                be liable for payment pursuant to the Terms.{" "}
              </p>{" "}
            </li>
            <li>
              <b>
                No More Than Price™ Payment for Your Order through Legrande.
              </b>
              <br />
              <p>
                If you use our "No More Than Price" option, we will estimate the
                maximum cost of your Order and we will pre-authorize your
                payment card for the "No More Than Price". The actual price of
                your Order may be equal to or less than the "No More Than Price"
                We will not charge you more than the "No More Than Price" amount
                for any individual Order without your prior consent.{" "}
              </p>
            </li>
            <li>
              <b></b>No Refunds or Returns.
              <br />
              <p>
                It is important that you carefully review any order or renewal
                for recurring prescriptions that you place. Once the product has
                been shipped, it cannot be returned and we offer no refund on
                shipped products. Once placed, and except as expressly set forth
                herein, all Orders are non-cancellable, non-refundable, and
                non-returnable.{" "}
              </p>
            </li>
            <li>
              <b>Cancellation of Your Prescription, Order or Refill.</b>
              <br />
              <p>
                You may remove a prescription from your Account or cancel a
                refill for a particular prescription at any time by calling the
                phone number set forth on the Services before an Order is placed
                for that prescription.
              </p>
            </li>
            <li>
              <b>Proprietary Rights.</b>
              <br />
              <p>
                Legrande and its licensors exclusively own all right, title and
                interest in and to the Services, including all associated
                intellectual property rights and any content provided by or on
                behalf of Legrande therein. You acknowledge that the Services
                are protected by laws of the United States and any applicable
                jurisdictions. You agree not to remove, alter or obscure any
                copyright, trademark, service mark or other proprietary rights
                notices incorporated in or accompanying the Services. Except as
                expressly provided in these Terms, nothing contained herein
                shall be construed as conferring any license or right, by
                implication, estoppel or otherwise, under copyright or other
                intellectual property rights.{" "}
              </p>
            </li>
            <li>
              <b>General Prohibitions.</b>
              <br />
              <p>You agree not to do any of the following</p>
              <ul className="list-disc my-4 flex flex-col gap-2">
                <li className="ml-12">
                  Use, display, mirror or frame the Services, or any individual
                  element within the Services, Legrande's names, any Legrande
                  trademark, logo or other proprietary information, or the
                  layout and design of any page or form contained on a page,
                  without Legrande's express written consent;{" "}
                </li>
                <li className="ml-12">
                  Access, tamper with, or use non-public areas of the Services,
                  Legrande's computer systems, or the technical delivery systems
                  of Legrande's providers;{" "}
                </li>
                <li className="ml-12">
                  Attempt to probe, scan, or test the vulnerability of any
                  Legrande system or network or breach any security or
                  authentication measures;{" "}
                </li>
                <li className="ml-12">
                  Attempt to access or search the Services through the use of
                  any engine, software, tool, agent, device or mechanism
                  (including spiders, robots, crawlers, data mining tools or the
                  like) other than the software and/or search agents provided by
                  Legrande or other generally available third party web
                  browsers;{" "}
                </li>
                <li className="ml-12">
                  Use the Services for any commercial purpose or for the benefit
                  of any third party or in any manner, other than as explicitly
                  permitted by these Terms;{" "}
                </li>
                <li className="ml-12">
                  Attempt to decipher, decompile, disassemble or reverse
                  engineer any of the software used to provide the Services;
                </li>
                <li className="ml-12">
                  Interfere with, or attempt to interfere with, the access of
                  any user, host or network, including, without limitation,
                  sending a virus, overloading, flooding, spamming, or
                  mail-bombing the Services;{" "}
                </li>
                <li className="ml-12">
                  Collect or store any personally identifiable information from
                  the Services from other users of the Services without their
                  express permission;{" "}
                </li>
                <li className="ml-12">
                  Impersonate or misrepresent your affiliation with any person
                  or entity;{" "}
                </li>
                <li className="ml-12">
                  Violate any applicable law or regulation; or{" "}
                </li>
                <li className="ml-12">
                  Encourage or enable any other individual to do any of the
                  foregoing;{" "}
                </li>
              </ul>
              <p className="my-4">
                Although we're not obligated to monitor access to or use of the
                Services, we have the right to do so for the purpose of
                operating the Services, to ensure compliance with these Terms,
                or to comply with applicable law or other legal requirements. We
                have the right to investigate violations of these Terms or
                conduct that affects the Services. We may also consult and
                cooperate with law enforcement authorities to prosecute users
                who violate the law.
              </p>
              <p className="my-4">
                The Services are provided for your individual, personal, and
                non-commercial use. You may not download, copy, sell, print,
                rent, lease, issue, distribute, transmit, broadcast, modify,
                perform, display, transfer, upload, post, create derivative
                works of, exploit, sublicense or otherwise assign the Services
                or any portion thereof to any third party. Nor may you remove
                any proprietary notices from the Services or attempt to disable,
                bypass, modify, defeat, or otherwise circumvent any digital
                rights management or security system used as part of the
                Services or attempt to access any restricted services or
                features.{" "}
              </p>
            </li>
            <li>
              <b>
                Additional Terms; Third-Party Websites, Products, and Services.
              </b>
              <br />
              <p>
                In addition to any terms provided with an Update, you may be
                required to agree to additional terms if you desire to access
                certain Services or features (collectively the "
                <b>Additional Terms</b>"). Except as expressly set forth in
                these Terms, if there is a conflict between these Terms and any
                Additional Terms, the Additional Terms will govern. You may also
                agree to Notice of Privacy Practices from us, or your doctor or
                dispensary, and if there is any conflict between these Terms,
                the Privacy Policy, and the Notice of Privacy Practices, the
                Notice of Privacy Practices shall prevail.
              </p>{" "}
              <p className="my-4">
                You may need to obtain additional products or services in order
                to use the Services, such as a device, internet access, or a
                data connection. You must obtain or use these products or
                services separately and pay all associated charges (including
                for internet access or other data transmission). Legrande does
                not endorse or sponsor any third-party products or services and
                does not have control over such products or services. Legrande
                is not responsible for any malfunction or error, including any
                unauthorized hacking, attributable to your use of a third-party
                product or service.
              </p>{" "}
              <p className="my-4">
                The Services may contain links or references to third-party web
                sites or other embedded resources (collectively, "
                <b>Linked Properties</b>"). Linked Properties may have their own
                terms of use, which Legrande does not control. Legrande does not
                monitor, endorse, or guarantee, and has no responsibility for,
                Linked Properties or any transactions or information you may
                undertake or receive from Linked Properties. If you have a
                specific dispute or concern related to the content, products, or
                services in Linked Properties, you should contact the owner of
                the Linked Property and not Legrande.{" "}
              </p>
            </li>
            <li>
              <b>User Content.</b>
              <br />
              <p>
                The Services may contain user profiles and other interactive
                features that allow users to post, upload, provide, or otherwise
                submit User Content on or through the Services. Legrande does
                not control and is not responsible or liable for any User
                Content. You acknowledge, represent, and agree that all User
                Content submitted through your Account ("<b>Your Content</b>")
                is submitted voluntarily and is not confidential or proprietary
                and that Legrande will not treat it as confidential. Legrande
                does not claim ownership of User Content. You grant to Legrande
                and its affiliates and service providers a worldwide,
                royalty-free, transferrable, sublicensable, non-exclusive,
                perpetual, irrevocable license under all of your intellectual
                property rights to make, use, copy, modify, adapt, create
                derivative works of, publicly perform or display, import,
                broadcast, transmit, distribute, license, publish, and translate
                Your Content (and derivative works thereof) in connection with
                the operation of the Services. Legrande is under no obligation
                to post User Content on the Services and Legrande may, in its
                discretion, edit, block, refuse to post, or remove any User
                Content (including Your Content) at any time. You represent and
                warrant that you have the rights necessary to grant the rights
                in this Section and that Your Content does not infringe the
                rights of Legrande or any third party or violate any agreement
                with or policy of Legrande or any applicable law. If Legrande
                suspects violations of the foregoing, Legrande may institute
                legal action and cooperate with law enforcement authorities in
                bringing legal proceedings against violators. You are solely
                responsible for Your Content and any other User Content you
                provide to Legrande or make available through the Services.
                Legrande is not obligated to review User Content; however, you
                authorize Legrande to screen, filter, restrict, block, move,
                refuse, and modify User Content at any time in Legrande's
                discretion and without notice. You also authorize Legrande to
                install, implement, manage, and operate one or more software,
                monitoring, or other solutions designed to assist in identifying
                and tracking User Content and violations of these Terms.{" "}
              </p>
            </li>
            <li>
              <b>Remedies and Corrective Action.</b>
              <br />
              <p>
                Legrande reserves the right to take any or all action it may
                deem appropriate in its sole discretion with respect to
                violations or enforcement of these Terms, and expressly reserves
                all rights and remedies available to Legrande at law or in
                equity, including instituting legal action and cooperating with
                law enforcement authorities. You agree to cooperate with
                Legrande in investigating suspected violations of these Terms by
                you or others. To the maximum extent authorized under applicable
                law, Legrande reserves the right to change, remove, delete,
                restrict, block access to, or stop providing any or all of the
                Services and the Content to you at any time and without
                notice.{" "}
              </p>
            </li>
            <li>
              <b>Availability; Support.</b>
              <br />
              <p>
                Legrande may, but has no obligation to, provide updates,
                supplements, or add-on components (collectively, "<b>Updates</b>
                ") to the Services, these Terms will apply to the Updates unless
                Legrande provides additional terms along with the Update in
                which case, those additional terms will apply to the Update. You
                agree that Legrande may automatically check your version of the
                Services and send Updates to the Services and may release
                subsequent versions of the Services and require you to the use
                the most current version. Legrande reserves the right to
                discontinue your access to the Services or to any Content
                provided or made available to you through use of the Services at
                any time for any reason.
              </p>{" "}
              <p className="my-4">
                Legrande has no obligation to, and might not, provide support in
                relation to the Services or your Account. Legrande does not
                guarantee availability of the Services or your Account and your
                access is permitted only if and when the Services and your
                Account are available. Your use of the Services and access to
                your Account may occasionally be restricted for service,
                upgrades, maintenance, or other reasons.
              </p>
            </li>
            <li>
              <b>Beta Service Disclaimer.</b>
              <br />
              <p>
                You acknowledge that: (i) the Services (or portions of the
                Services) made available under these Terms may be in a beta
                development stage or may have otherwise not been made
                commercially available by Legrande; (ii) the Services may not
                operate properly, be in final form or fully functional; (iii)
                the Services may contain errors, design flaws or other problems;
                (iv) Legrande has no obligation to release a commercial version
                of the Services; and (v) Legrande has the right unilaterally to
                abandon development of all or a part of the Services, at any
                time and without any obligation or liability to you
              </p>
            </li>
            <li>
              <b>Termination.</b>
              <br />
              <p>
                We may terminate your access to and use of the Services or
                deactivate or cancel your Account, at our sole discretion with
                or without notice, for any reason or no reason, and without
                liability to you. You may cancel your Account at any time by
                sending an email to us at support@legrandehealth.com. Upon any
                termination, discontinuation or cancellation of Services or your
                Account, all provisions of these Terms which by their nature
                should survive will survive, including, without limitation,
                ownership provisions, warranty disclaimers, limitations of
                liability, and dispute resolution provisions. Upon any
                termination, discontinuation or cancellation of the Services or
                your Account by you or us, you will continue to be obligated to
                pay all amounts owing under these Terms which are due and
                payable prior to such termination, discontinuation or
                cancellation. Sections 10, 12, 13, 16, 20-23, 25, and 26-28 of
                these Terms will survive termination.{" "}
              </p>
            </li>
            <li>
              <b>Disclaimer of Warranties.</b>
              <br />
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE SERVICES
                AND ALL CONTENT IS PROVIDED "AS IS," "WITH ALL FAULTS," AND "AS
                AVAILABLE" WITHOUT WARRANTY OF ANY KIND AND THE ENTIRE RISK OF
                USE AND PERFORMANCE, REMAINS WITH YOU. LEGRANDE DOES NOT MAKE
                ANY REPRESENTATIONS, WARRANTIES, OR CONDITIONS, EXPRESS,
                IMPLIED, OR STATUTORY AND HEREBY DISCLAIMS ANY IMPLIED
                WARRANTIES OR CONDITIONS OF MERCHANTABILITY, MERCHANTABLE
                QUALITY, DURABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
                QUIET ENJOYMENT, OR NON-INFRINGEMENT AND ANY WARRANTIES ARISING
                OUT OF COURSE OF DEALING OR USAGE OF TRADE. IN PARTICULAR,
                LEGRANDE MAKES NO WARRANTY THAT THE SERVICES, SERVICES OR
                CONTENT: (A) WILL MEET YOUR REQUIREMENTS; (B) WILL BE AVAILABLE
                OR PROVIDED ON AN UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE
                BASIS; (C) WILL BE ACCURATE, COMPLETE, RELIABLE, TRUTHFUL,
                INCLUDING AS IT RELATES TO ANY HEALTH-RELATED CONTENT OBTAINED
                AS A RESULT OF YOUR USE OF THE SERVICESOR (D) WILL BE FREE FROM
                VIRUSES, WORMS, OR OTHER HARMFUL OR MALICIOUS COMPONENTS.
              </p>{" "}
              <p className="my-4">
                IT IS SOLELY YOUR RESPONSIBILITY TO (1) ENSURE THAT YOUR ORDER
                WAS PROPERLY PROCESSED AND THAT YOU RECEIVED THE CORRECT
                PRODUCTS (INCLUDING THE CORRECT DOSAGE(S) THEREOF), (2) CONSUME
                THE PROPER DOSAGE(S) OF YOUR PRODUCTS(S), AND (3) STORE ALL
                PRODUCTS PROPERLY AND IN ACCORDANCE WITH THE GUIDELINES PROVIDED
                BY THE DISPENSARY AND/OR APPLICABLE PRODUCT MANUFACTURER(S).
              </p>{" "}
              <p className="my-4">
                LEGRANDE EXPRESSLY DISCLAIMS ANY REPRESENTATIONS OR WARRANTIES,
                WHETHER EXPRESS OR IMPLIED, REGARDING A DISPENSARY'S AND ITS
                PERSONNEL'S PROFESSIONAL QUALIFICATIONS, EXPERTISE, QUALITY OF
                WORK, OR ACCURACY IN FILLING YOUR PRESCRIPTION(S), AND ASSUMES
                NO RESPONSIBILITY FOR A DISPENSARY'S COMPLIANCE OR
                NON-COMPLIANCE WITH ANY APPLICABLE LAWS AND REGULATIONS. WITHOUT
                LIMITING THE FOREGOING, LEGRANDE EXPRESSLY DISCLAIMS ALL
                LIABILITY FOR ANY PRODUCTS INCORRECTLY FILLED BY A DISPENSARY,
                WHETHER AS A RESULT OF INACCURATE INFORMATION PROVIDED BY YOU OR
                OTHERWISE, ANY CANCELLED OR OTHERWISE UNFILLED PRODUCTS (OR ANY
                INJURY OR DEATH RESULTING THEREFROM), OR ANY ACT OR OMISSION OF
                ANY DISPENSARY.{" "}
              </p>
            </li>
            <li>
              <b>Limitation of Damages. </b>
              <br />
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
                WILL LEGRANDE BE LIABLE FOR ANY CONSEQUENTIAL, SPECIAL,
                INCIDENTAL, INDIRECT, OR PUNITIVE DAMAGES; FOR LOSS OF PROFITS,
                BUSINESS, GOODWILL, ANTICIPATED SAVINGS, OR USE; LOSS OR
                CORRUPTION OF DATA, CONFIDENTIAL INFORMATION, OR OTHER
                INFORMATION; BUSINESS INTERRUPTION; PERSONAL INJURY; WRONGFUL
                DEATH; PROPERTY DAMAGE; LOSS OF PRIVACY; FAILURE TO MEET ANY
                DUTY OF GOOD FAITH OR REASONABLE CARE; NEGLIGENCE; AND ANY OTHER
                PECUNIARY OR OTHER LOSS WHATSOEVER, ARISING OUT OF, BASED ON,
                RESULTING FROM, OR IN ANY WAY RELATED TO THESE TERMS, THE
                WEBSITE, THE CONTENT, OR YOUR ACCOUNT, EVEN IF LEGRANDE HAS BEEN
                ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </p>{" "}
              <p className="my-4">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, LEGRANDE'S
                MAXIMUM, AGGREGATE LIABILITY TO YOU, AND YOUR EXCLUSIVE REMEDY
                UNDER THESE TERMS FOR ANY AND ALL DAMAGES, INJURIES, AND LOSSES
                ARISING FROM ANY AND ALL CLAIMS AND CAUSES OF ACTION ARISING OUT
                OF, BASED ON, RESULTING FROM OR IN ANY WAY RELATED TO THESE
                TERMS, THE WEBSITE, THE CONTENT, OR YOUR ACCOUNT WILL NOT EXCEED
                $100. THE EXISTENCE OF MULTIPLE CLAIMS OR SUITS UNDER THESE
                TERMS OR RELATED TO THE WEBSITE OR CONTENT WILL NOT ENLARGE OR
                EXTEND THE LIMITATION OF MONEY DAMAGES.{" "}
              </p>{" "}
              <p className="my-4">
                DAMAGES. NOTHING IN THIS SECTION WILL BE INTERPRETED AS
                EXCLUDING LIABILITY WHICH CANNOT UNDER APPLICABLE LAW BE
                EXCLUDED.{" "}
              </p>
            </li>
            <li>
              <b>Indemnity. </b>
              <br />
              <p>
                To the maximum extent allowed by applicable law, you agree to
                defend, indemnify, and hold harmless Legrande, its directors,
                officers, employees, agents, partners, suppliers, affiliates,
                and licensors, from and against all claims, suits, proceedings,
                losses, liabilities, and expenses, including attorneys' fees and
                costs, whether in tort, contract or otherwise, relating to,
                arising from, or allegedly arising from (a) your use of the
                Services, Services and the Content; (b) activities occurring
                under your Account; (c) any violation of these Terms; (d) Your
                Content; and (e) your violation of any other party's rights or
                applicable law. Legrande reserves the right to assume, at its
                sole expense, the exclusive defense and control of any matter
                subject to indemnification by you, in which event you will fully
                cooperate with Legrande in asserting any available defenses{" "}
              </p>
            </li>
            <li>
              <b>Notices. </b>
              <br />
              <p>
                Legrande may give you all required notices (including legal
                process) by any lawful method, including by posting notices on
                the Services or by sending it to any email address that you
                provide to Legrande. You agree to send Legrande notices at{" "}
                <a
                  className="text-primary"
                  href={`mailto:support@legrandehealth.com`}
                >
                  support@legrandehealth.com.
                </a>{" "}
                You may also write Legrande at 18 Bunsen, Irvine, CA 92618.{" "}
              </p>
            </li>
            <li>
              <b>Changes to these Terms.</b>
              <br />
              <p>
                Legrande reserves the right to change these Terms at any time
                upon notice to you. You agree to periodically review these Terms
                for changes and you can review the most current Terms at any
                time at legrandehealth.app/terms-of-use. Updated Terms are
                binding on you as of the effective date indicated in our notice.
                Your continued use of the Services after the effective date
                indicates your acceptance of the updated Terms, even if you have
                not reviewed them. If, at any time, you do not agree to the
                Terms, you should disable your Account and stop using the
                Services.{" "}
              </p>
            </li>
            <li>
              <b>Governing Law. </b>
              <br />
              <p>
                The United Nations Convention on Contracts for the International
                Sale of Goods will not apply to these Terms. California law will
                govern the interpretation of these Terms and applies to all
                claims under or for breach of these Terms or relating to the
                Services or Content (regardless of conflict of laws principles),
                except where the application of another law cannot be prevented
                by agreement.{" "}
              </p>
            </li>
            <li>
              <b>
                AGREEMENT TO ARBITRATE; CLASS ACTION WAIVER; AND LOCATION OF
                DISPUTE RESOLUTION. You and Legrande each agree that all claims,
                controversies, and disputes ("Disputes") between You and
                Legrande, including Disputes arising out of or related to the
                Services, Services, Orders, Content, or these Terms, will be
                resolved by binding arbitration by a single neutral arbitrator,
                provided, however, that the arbitration provision in this
                Section ("Arbitration Provision") does not preclude You or
                Legrande from asserting any claim that may be brought in small
                claims court arising out of any Disputes. YOU ALSO ACKNOWLEDGE
                THAT YOU AND LEGRANDE ARE EACH AGREEING TO FOREGO THE ABILITY TO
                PURSUE DISPUTES IN COURT, OTHER THAN SMALL CLAIMS COURT,
                INCLUDING THE ABILITY TO HAVE A JURY DECIDE ANY DISPUTES. Unless
                you and Legrande otherwise agree, JAMS will arbitrate all
                disputes and JAMS rules will apply. You and Legrande each agree
                to only pursue Disputes on an individual basis and will not
                pursue Disputes, whether in arbitration or otherwise, on a
                class, consolidated, or representative basis, regardless of the
                application of procedural rules. YOU ALSO ACKNOWLEDGE THAT YOU
                AND LEGRANDE ARE EACH AGREEING TO FOREGO THE ABILITY TO PROCEED
                IN CLASS ACTIONS, CONSOLIDATED ACTIONS, OR REPRESENTATIVE
                ACTIONS, EITHER AS A REPRESENTATIVE OF OR MEMBER OF A CLASS.
              </b>
              <br />
              <p className="font-semibold my-4">
                Notwithstanding the above and any JAMS rule to the contrary, or
                any other provision in arbitration rules chosen by agreement to
                govern the arbitration, you and Legrande each agree that any
                challenges to the validity or enforceability of the class action
                waiver in this Arbitration Provision will be decided by a
                federal court and not by an arbitrator. If any court or
                arbitrator holds that the class action waiver in this
                Arbitration Provision is unenforceable, then the Dispute must be
                brought in a state or federal court and not in arbitration.
              </p>{" "}
              <p className="font-semibold my-4">
                You and Legrande each agree that the location of the arbitration
                or court where the Dispute will be resolved will be the largest
                city in the state where you live. You and Legrande each also
                agree that for arbitrated Disputes, the arbitrator's award will
                be final and binding and may be entered as a judgment in any
                court of competent jurisdiction. An arbitrator may award on an
                individual basis any relief authorized by law, including
                injunctive or declaratory relief and reasonable attorneys' fees.
                You and Legrande each are responsible for your or its respective
                costs relating to the arbitration, except that Legrande will pay
                all arbitration administrative or filing fees, including the
                arbitrator fees, other than, in a Dispute in which you assert a
                claim against Legrande, the amount of the fees that equals the
                then-applicable fee for filing a civil action in federal court
                for the judicial district in which you live, unless you
                demonstrate to the arbitrator that you would be entitled to file
                that civil action without payment of the then-applicable fee as
                provided by law. To begin arbitration, you must send a letter
                requesting arbitration and describing your Dispute to the
                contact listed for Legrande herein. You may opt out of this
                Arbitration Provision by mailing Legrande written notice of your
                election to opt-out to the attention of Legrande Legal
                Department at the address listed herein within 45 days of the
                date you first use any of the Services, Services, Orders or
                Content expressly indicating that you are opting out of the
                Arbitration Provision and including your name and address,
                provided that your election will not be effective until it is
                received by Legrande. Your election to opt-out of this
                Arbitration Provision will not otherwise affect your rights and
                obligations under these Terms. You and Legrande each also agree
                that these terms affect interstate commerce so the Federal
                Arbitration Act, not state law, applies including with respect
                to any question of whether a Dispute is subject to arbitration
                (despite the choice of governing law herein).{" "}
              </p>
            </li>
            <li>
              <b>Feedback.</b>
              <br />
              <p>
                You have no obligation to—but may provide—suggestions, comments,
                or other feedback to Legrande with respect to the Services,
                Services, Orders or Content or Legrande's products or services
                ("<b>Feedback</b>"). You agree that all Feedback is given
                voluntarily and it is not and will not be treated as
                confidential even if you designate it as confidential. You will
                not give Feedback that is subject to license terms that seek to
                require any Legrande product, technology, service, or
                documentation incorporating or derived from Feedback, or any
                Legrande intellectual property, to be licensed or otherwise
                shared with any third party. You hereby assign to Legrande and
                rights in or to any Feedback. Legrande will be free to use,
                disclose, reproduce, license, or otherwise distribute and
                exploit the Feedback provided to Legrande through any manner or
                means, as it sees fit, entirely without obligation to you or
                restriction of any kind on account of intellectual property
                rights or otherwise.{" "}
              </p>
            </li>
            <li>
              <b>General. </b>
              <br />
              <p>
                These Terms constitute the entire and exclusive understanding
                and agreement between Legrande and you regarding the Services,
                Services, Orders, or Content and these Terms supersede and
                replace any and all prior oral or written understandings or
                agreements between Legrande and you regarding the Services,
                Services, Orders and/or Content. If any provision of these Terms
                is held invalid or unenforceable (either by an arbitrator
                appointed pursuant to the terms of the "Arbitration" section
                above or by court of competent jurisdiction, but only if you
                timely opt out of arbitration by sending us an Arbitration
                Opt-out Notice in accordance with the terms set forth above),
                that provision will be enforced to the maximum extent
                permissible and the other provisions of these Terms will remain
                in full force and effect. You and Legrande intend that the
                provisions of these Terms be enforced to the fullest extent
                permitted by applicable law. Accordingly, you and Legrande agree
                that if any provision is deemed unenforceable, where possible,
                it will be modified to the extent necessary to make it
                enforceable, which may include its deletion. You may not assign,
                transfer, or sublicense your rights, if any, in your Account or
                under these Terms. Legrande's failure to enforce any right or
                provision of these Terms will not be considered a waiver of
                those rights. The waiver of any such right or provision will be
                effective only if in writing and signed by a duly authorized
                representative of Legrande.{" "}
              </p>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
